/**
 * The Copilot panel — the in-app agent, opened from the command palette.
 *
 * A React panel inside the app rather than a tab in the support widget. The
 * widget is a framework-free IIFE also served to anonymous marketing sites; it
 * cannot render platform components, cannot stream, and spans a trust boundary.
 * This lives inside the design system and consumes the SSE stream, so tool
 * steps appear as they run instead of behind a typing dot for fifteen seconds.
 *
 * Phase 4 adds the confirmation card. When the Copilot wants to do something it
 * does NOT do it — the server returns the action gated, and the card here is
 * where a human reads it and decides. Confirm sends the tool and arguments back
 * verbatim to /confirm, which runs exactly that call with the model out of the
 * loop. There is no "approved" flag on /ask, on purpose: a boolean the client
 * sets would let any UI (or any script) run every action unconfirmed, and
 * re-asking the model to "do it now" cannot promise the call it makes is the
 * one the human read.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  History,
  Loader2,
  Maximize2,
  Mic,
  Minimize2,
  Pin,
  PinOff,
  Send,
  Sparkles,
  Square,
  Undo2,
  Volume2,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  askCopilot,
  offerCopilotAction,
  tickPlanStep,
  type CopilotPlan,
  type CopilotPlanResult,
  type CopilotPlanStep,
  type CopilotTask,
  type CopilotTaskListResult,
  fetchCopilotConversation,
  fetchCopilotConversations,
  pinCopilotConversation,
  type CopilotConversation,
  cancelCopilotRun,
  fetchCopilotRuns,
  startCopilotRun,
  RUN_IS_OPEN,
  type CopilotRun,
  confirmCopilotAction,
  fetchCopilotCapabilities,
  type CopilotCapabilities,
  type CopilotEvent,
  type CopilotTurn,
  type PendingApproval,
} from '@/services/copilotStream';
import { DEFAULT_ASSISTANT_NAME } from './useCopilotLauncher';
import { useCopilotVoice } from './useCopilotVoice';

interface CopilotPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Seed question, e.g. what the user typed into the palette. */
  initialQuestion?: string;
  /** The tab the user was on — framing for the answer, never scope. */
  pageContext?: string;
}

interface Turn extends CopilotTurn {
  /** Tools this answer ran, for the trace under it. */
  steps?: string[];
  failed?: boolean;
  /** Actions the Copilot proposed and did not take. */
  approvals?: PendingApproval[];
  /** A tool's own output, rendered by the panel rather than described in prose. */
  taskList?: CopilotTaskListResult;
  /** A plan the agent proposed for something it cannot do itself. */
  plan?: CopilotPlan;
  /** Steps dropped because they were outside this caller's reach. */
  planOmitted?: number;
}

/** One card's lifecycle. Tracked per card so two cards in one answer decide
 *  independently, and so a confirmed card cannot be confirmed twice. */
type CardState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'done'; message: string }
  | { kind: 'dismissed' }
  | { kind: 'failed'; message: string };

/** `nav.find_page` → "Finding the right page". Tool names are internal. */
const STEP_LABELS: Record<string, string> = {
  'nav.find_page': 'Finding the right page',
  'nav.explain_feature': 'Checking what that feature does',
  'nav.search_kb': 'Searching help articles',
  'analytics.caseload_summary': 'Counting your caseload',
  'analytics.program_summary': 'Reading your programs',
  'member.search': 'Searching members you can see',
  'member.get_profile': 'Opening that member',
  'task.create': 'Creating a task',
};

/** Argument keys the user should read, in the order a person would say them.
 *  An allowlist rather than "render every key": an argument the model invented
 *  is not something to show a human as though the system meant it. */
const ARG_LABELS: [string, string][] = [
  ['title', 'Task'],
  ['member_id', 'Member'],
  ['priority', 'Priority'],
  ['deadline', 'Due'],
  ['description', 'Notes'],
];

/** What the human is promised about taking it back. Drives the card's tone —
 *  an irreversible action should not look like an undoable one. */
const REVERSIBILITY_NOTE: Record<string, string> = {
  undoable: 'You can undo this afterwards.',
  compensable: 'This can be corrected afterwards, but not simply undone.',
  irreversible: 'This cannot be undone.',
};

function stepLabel(tool: string): string {
  return STEP_LABELS[tool] || tool;
}

/**
 * What Wren is doing that nobody is watching.
 *
 * The panel can stream exactly one turn. Anything asked while it is mid-turn
 * becomes a run — work on a worker that outlives this tab — and this is where
 * those appear. Without it a background run is indistinguishable from the
 * question having been swallowed, which is what used to happen.
 *
 * Finished runs stay listed, because the answer to something asked ten minutes
 * ago is the reason for starting it in the background.
 */
function RunTray({
  runs,
  onCancel,
  t,
}: {
  runs: CopilotRun[];
  onCancel: (id: string) => void;
  t: (k: string, d: string) => string;
}) {
  if (runs.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {t('copilot.backgroundWork', 'Working on')}
      </p>
      {runs.map((run) => {
        const open = RUN_IS_OPEN(run.status);
        return (
          <div
            key={run.id}
            className="rounded-md border border-gray-200 px-3 py-2 text-xs dark:border-gray-700"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="flex-1 truncate text-gray-700 dark:text-gray-200">{run.prompt}</p>
              {open && (
                <button
                  type="button"
                  onClick={() => onCancel(run.id)}
                  className="shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  {t('copilot.cancel', 'Cancel')}
                </button>
              )}
            </div>
            <p className="mt-1 text-gray-500 dark:text-gray-400">{runStatusLabel(run, t)}</p>
            {/* The answer, once there is one. A tray that only ever says
                "done" sends the user looking for where the result went. */}
            {run.summary && !open && (
              <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-200">{run.summary}</p>
            )}
            {run.error && <p className="mt-1 text-red-700 dark:text-red-300">{run.error}</p>}
          </div>
        );
      })}
    </div>
  );
}

function runStatusLabel(run: CopilotRun, t: (k: string, d: string) => string): string {
  switch (run.status) {
    case 'queued':
      return t('copilot.runQueued', 'Queued…');
    case 'running':
      return run.steps.length
        ? stepLabel(run.steps[run.steps.length - 1].tool)
        : t('copilot.thinking', 'Thinking…');
    case 'awaiting_confirmation':
      // Distinct from "done" on purpose: something is waiting on this person,
      // and a tray that calls it finished is a card nobody goes back to.
      return t('copilot.runNeedsYou', 'Needs your confirmation');
    case 'cancelled':
      return t('copilot.runCancelled', 'Cancelled');
    case 'failed':
      return t('copilot.runFailed', 'Did not finish');
    default:
      return t('copilot.runDone', 'Done');
  }
}

/**
 * Past conversations, so persistence is something a person can see.
 *
 * The server has owned transcripts for a while, but nothing surfaced them — and
 * storage nobody can reach is indistinguishable from no storage at all.
 *
 * Pinned threads sort first and carry a filled pin. The pin is not only
 * ordering: a pinned conversation is exempt from the retention sweep, which is
 * why it is worth a control rather than an automatic "keep recent" rule.
 */
function ConversationList({
  conversations,
  loading,
  activeId,
  onOpen,
  onTogglePin,
  onNew,
  t,
}: {
  conversations: CopilotConversation[];
  loading: boolean;
  activeId: string | null;
  onOpen: (id: string) => void;
  onTogglePin: (id: string, next: boolean) => void;
  onNew: () => void;
  t: (k: string, d: string) => string;
}) {
  return (
    <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
      <button
        type="button"
        onClick={onNew}
        className="w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
      >
        {t('copilot.newConversation', 'Start a new conversation')}
      </button>

      {loading && (
        <p className="flex items-center gap-2 py-4 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          {t('common.loading', 'Loading…')}
        </p>
      )}

      {!loading && conversations.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {t('copilot.noHistory', 'No past conversations yet.')}
        </p>
      )}

      {conversations.map((c) => (
        <div
          key={c.id}
          className={
            'flex items-start gap-2 rounded-md border px-3 py-2 ' +
            (c.id === activeId
              ? 'border-[#0D9488] bg-teal-50/60 dark:bg-teal-900/20'
              : 'border-gray-200 dark:border-gray-700')
          }
        >
          <button
            type="button"
            onClick={() => onOpen(c.id)}
            /* Disabled rather than hidden: the thread is still listed because
               it was pinned, and saying its content expired is better than
               quietly dropping it from a list the person curated. */
            disabled={c.purged}
            className="min-w-0 flex-1 text-left disabled:opacity-60"
          >
            <p className="truncate text-sm text-gray-800 dark:text-gray-100">
              {c.opening || t('copilot.untitled', 'Untitled conversation')}
            </p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {c.purged
                ? t('copilot.historyExpired', 'Content expired')
                : `${c.message_count} ${t('copilot.messages', 'messages')}`}
              {c.last_turn_at ? ` · ${new Date(c.last_turn_at).toLocaleDateString()}` : ''}
            </p>
          </button>
          <button
            type="button"
            onClick={() => onTogglePin(c.id, !c.pinned)}
            aria-label={c.pinned ? t('copilot.unpin', 'Unpin') : t('copilot.pin', 'Pin')}
            aria-pressed={c.pinned}
            title={
              c.pinned
                ? t('copilot.unpinHint', 'Unpin — this conversation will expire normally again')
                : t('copilot.pinHint', 'Pin — keeps this conversation past the usual retention window')
            }
            className={
              'shrink-0 rounded p-1 ' +
              (c.pinned
                ? 'text-[#0D9488]'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200')
            }
          >
            {c.pinned ? <Pin className="h-4 w-4 fill-current" /> : <PinOff className="h-4 w-4" />}
          </button>
        </div>
      ))}
    </div>
  );
}

const TASK_STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked: 'Blocked',
  completed: 'Completed',
};

/** Only an unfinished task can be closed, so only those get the control. */
const TASK_IS_OPEN = (status: string) => status !== 'completed' && status !== 'cancelled';

/**
 * The tasks a tool actually returned, rendered as rows.
 *
 * It replaces a markdown table the MODEL typed into a panel that renders plain
 * text — which arrived as a wall of pipes and asterisks, and which no button
 * could be attached to. Rendering the tool's own data fixes both at once: it
 * cannot be mistyped, and each row is a place to put an action.
 */
function TaskListCard({
  result,
  busyId,
  onComplete,
  t,
}: {
  result: CopilotTaskListResult;
  busyId: number | null;
  onComplete: (task: CopilotTask) => void;
  t: (k: string, d: string) => string;
}) {
  if (!result.tasks?.length) {
    return (
      <p className="mt-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {t('copilot.noTasks', 'No tasks for this member.')}
      </p>
    );
  }

  return (
    <div className="mt-2 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
      {result.tasks.map((task, i) => (
        <div
          key={task.id}
          className={
            'flex items-start gap-2 px-3 py-2 ' +
            (i > 0 ? 'border-t border-gray-100 dark:border-gray-800 ' : '') +
            (task.status === 'completed' ? 'opacity-60' : '')
          }
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-gray-800 dark:text-gray-100">{task.title}</p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {/* Priority is only worth saying when it is not the default —
                  "Medium" on every row is noise that hides the urgent one. */}
              {t(`copilot.taskStatus.${task.status}`, TASK_STATUS_LABEL[task.status] || task.status)}
              {task.priority && task.priority !== 'medium' ? ` · ${task.priority}` : ''}
              {task.deadline ? ` · ${t('copilot.due', 'due')} ${task.deadline}` : ''}
            </p>
          </div>
          {TASK_IS_OPEN(task.status) && (
            <button
              type="button"
              onClick={() => onComplete(task)}
              disabled={busyId === task.id}
              className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:border-gray-400 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
            >
              {busyId === task.id
                ? t('common.loading', 'Loading…')
                : t('copilot.markComplete', 'Mark complete')}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A plan: what to do when the agent cannot do it.
 *
 * The catalogue is ten tools against roughly 4,963 routes, so "I don't have a
 * tool for that" is the common answer, not the rare one — and it is true and
 * useless to someone sitting on the screen where the thing can be done.
 *
 * A plan adds no authority. Manual steps are a person reading an instruction;
 * tool steps still raise an ordinary confirmation card. What changes is that
 * the refusal becomes a checklist.
 */
function PlanCard({
  plan,
  omitted,
  busyStep,
  onTick,
  onRun,
  t,
}: {
  plan: CopilotPlan;
  omitted: number;
  busyStep: number | null;
  onTick: (seq: number) => void;
  onRun: (step: CopilotPlanStep) => void;
  t: (k: string, d: string) => string;
}) {
  const remaining = plan.steps.filter((s) => !s.done).length;

  return (
    <div className="mt-2 rounded-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-baseline justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {plan.title}
        </p>
        <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
          {plan.status === 'done'
            ? t('copilot.planDone', 'All done')
            : `${remaining} ${t('copilot.planLeft', 'left')}`}
        </p>
      </div>

      <ol className="divide-y divide-gray-100 dark:divide-gray-800">
        {plan.steps.map((step) => (
          <li key={step.seq} className="flex items-start gap-2 px-3 py-2">
            {/* A checkbox, because the manual steps are the ones a person does
                and ticking is the whole interaction. Tool steps get a button
                instead — pressing them proposes the action, it does not do it. */}
            {step.kind === 'manual' ? (
              <input
                type="checkbox"
                checked={step.done}
                onChange={() => onTick(step.seq)}
                aria-label={step.instruction}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#0D9488]"
              />
            ) : (
              <span
                className={
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ' +
                  (step.done ? 'bg-[#0D9488] text-white' : 'border border-gray-300 dark:border-gray-600')
                }
                aria-hidden
              >
                {step.done ? '✓' : ''}
              </span>
            )}

            <div className="min-w-0 flex-1">
              <p
                className={
                  'text-sm ' +
                  (step.done
                    ? 'text-gray-400 line-through dark:text-gray-500'
                    : 'text-gray-800 dark:text-gray-100')
                }
              >
                {step.instruction}
              </p>
            </div>

            {step.kind === 'tool' && !step.done && (
              <button
                type="button"
                onClick={() => onRun(step)}
                disabled={busyStep === step.seq}
                className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:border-gray-400 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
              >
                {busyStep === step.seq
                  ? t('common.loading', 'Loading…')
                  : t('copilot.planRunStep', 'Do this')}
              </button>
            )}
          </li>
        ))}
      </ol>

      {/* Said without naming them: a step was dropped because the person cannot
          reach that screen or run that tool, and listing it would tell them a
          screen exists that they are not allowed to see. */}
      {omitted > 0 && (
        <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
          {t('copilot.planOmitted', 'Some steps need access you do not have — ask an administrator.')}
        </p>
      )}
    </div>
  );
}

interface ConfirmationCardProps {
  approval: PendingApproval;
  state: CardState;
  onConfirm: () => void;
  onDismiss: () => void;
}

/**
 * The card a human reads before anything happens.
 *
 * It renders the SERVER's description of the action, not a local one. A
 * per-tool description table in the frontend drifts the moment a tool changes
 * on the backend, and the way it drifts is a card that describes the wrong
 * action — the one failure a confirmation dialog cannot have.
 */
function ConfirmationCard({ approval, state, onConfirm, onDismiss }: ConfirmationCardProps) {
  const { t } = useTranslation();

  if (state.kind === 'dismissed') return null;

  if (state.kind === 'done') {
    return (
      <div className="mt-2 flex items-start gap-2 rounded-md bg-green-50 p-3 text-sm text-green-900 dark:bg-green-900/20 dark:text-green-200">
        <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p>{state.message}</p>
      </div>
    );
  }

  const irreversible = approval.reversibility === 'irreversible';
  const note = REVERSIBILITY_NOTE[approval.reversibility || ''];
  const running = state.kind === 'running';

  const rows = ARG_LABELS.map(([key, label]) => [label, approval.args?.[key]] as const).filter(
    ([, value]) => value !== undefined && value !== null && String(value).trim() !== '',
  );

  return (
    <div
      className={`mt-2 rounded-md border p-3 ${
        irreversible
          ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
          : 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
      }`}
    >
      <p className="flex items-start gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {approval.message ||
            t('copilot.confirmGeneric', 'The Copilot wants to do something. Confirm it first.')}
        </span>
      </p>

      {rows.length > 0 && (
        <dl className="mt-2 space-y-0.5 text-xs text-gray-700 dark:text-gray-300">
          {rows.map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <dt className="shrink-0 font-medium">{label}</dt>
              {/* The value the server will receive, shown exactly as it will be
                  sent — the point of the card is that these are the arguments
                  that run, not a paraphrase of them. */}
              <dd className="min-w-0 break-words">{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {note && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <Undo2 className="h-3 w-3" aria-hidden />
          {note}
        </p>
      )}

      {state.kind === 'failed' && (
        <p className="mt-2 text-xs text-red-700 dark:text-red-300">{state.message}</p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900"
        >
          {running && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
          {running ? t('copilot.confirming', 'Working…') : t('copilot.confirm', 'Confirm')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={running}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
        >
          {t('copilot.cancelAction', 'Not now')}
        </button>
      </div>
    </div>
  );
}

export function CopilotPanel({
  isOpen,
  onClose,
  initialQuestion,
  pageContext,
}: CopilotPanelProps) {
  const { t } = useTranslation();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeSteps, setActiveSteps] = useState<string[]>([]);
  const [caps, setCaps] = useState<CopilotCapabilities | null>(null);
  const [capsLoaded, setCapsLoaded] = useState(false);
  // Keyed by "<turn index>:<approval index>" so two cards in one answer decide
  // independently, and a confirmed card cannot be confirmed a second time.
  const [cardStates, setCardStates] = useState<Record<string, CardState>>({});
  // Work being done away from this connection. The panel can hold exactly one
  // streaming turn — that is what `busy` is — so anything asked while it is
  // busy becomes a run instead of being dropped, which is what it used to be.
  const [runs, setRuns] = useState<CopilotRun[]>([]);
  // Bumped to re-run the poll effect immediately after starting or cancelling,
  // rather than waiting out the current interval.
  const [runPollKey, setRunPollKey] = useState(0);
  // Which half of the panel is showing. History is a VIEW, not a second panel:
  // the conversation stays mounted underneath so returning to it does not
  // reload or lose an in-flight turn.
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [conversations, setConversations] = useState<CopilotConversation[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Side panel vs full page. Reading a long thread, or a run tray with several
  // entries, in a 512px column is the reason this exists.
  const [expanded, setExpanded] = useState(false);
  // Which task row is waiting on the server to raise its card.
  const [taskBusyId, setTaskBusyId] = useState<number | null>(null);
  // Which plan step is waiting on the server to raise its card.
  const [planBusyStep, setPlanBusyStep] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // The voice controller is created further down (it needs `caps`), and the
  // close effect above it has to be able to stop the microphone. A ref bridges
  // the two without moving a hook call, which would change hook order.
  const voiceStopRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The conversation this panel is continuing. A ref rather than state because
  // it is read inside the async send and must not rebuild the callback — and
  // because nothing renders from it.
  //
  // It replaces a `turnsRef` that existed only to re-send the whole transcript
  // on every question. The server keeps that now, which is both why a reload
  // can be made to resume and why the agent can finally be shown what its own
  // tools returned.
  const conversationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    fetchCopilotCapabilities(controller.signal).then((c) => {
      setCaps(c);
      setCapsLoaded(true);
    });
    return () => controller.abort();
  }, [isOpen]);

  // Abort any in-flight turn when the panel closes, so a half-streamed answer
  // does not land in a panel the user has left.
  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort();
      abortRef.current = null;
      setBusy(false);
      setActiveSteps([]);
      // And the microphone. A half-streamed answer landing in a closed panel is
      // untidy; a live mic the user has no control left to stop is not.
      voiceStopRef.current?.();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, activeSteps]);

  // Poll while anything is outstanding, and once on open so a run started in a
  // previous session is still visible. Stops when nothing is open, so an idle
  // panel is not a timer.
  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      const latest = await fetchCopilotRuns(controller.signal);
      if (controller.signal.aborted) return;
      setRuns(latest);
      if (latest.some((r) => RUN_IS_OPEN(r.status))) timer = setTimeout(tick, 3000);
    };
    void tick();

    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [isOpen, runPollKey]);

  // Loaded when history opens, not on every panel open: most sessions never
  // look at it, and a list of someone's past conversations is not something to
  // fetch speculatively.
  const openHistory = useCallback(async () => {
    setView('history');
    setHistoryLoading(true);
    const rows = await fetchCopilotConversations();
    setConversations(rows);
    setHistoryLoading(false);
  }, []);

  const resumeConversation = useCallback(async (conversationId: string) => {
    setHistoryLoading(true);
    const stored = await fetchCopilotConversation(conversationId);
    setHistoryLoading(false);
    if (stored.length === 0) return;

    // Replace rather than append. Reopening a thread means being in it, not
    // stacking it on top of whatever was on screen.
    conversationRef.current = conversationId;
    setTurns(
      stored.map((m) => ({
        role: m.role,
        content: m.content,
        steps: m.steps?.length ? m.steps.map((st) => st.tool) : undefined,
      })),
    );
    // Cards are not restored. A confirmation offered minutes or days ago has
    // expired server-side, so rendering it would be a button that cannot work —
    // and the transcript already records whichever way it was answered.
    setCardStates({});
    setView('chat');
  }, []);

  const togglePin = useCallback(async (conversationId: string, next: boolean) => {
    // Optimistic, and re-sorted immediately: a pin that visibly does nothing
    // until a refetch reads as not having registered.
    setConversations((prev) => {
      const updated = prev.map((c) => (c.id === conversationId ? { ...c, pinned: next } : c));
      return [...updated].sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return (b.last_turn_at || '').localeCompare(a.last_turn_at || '');
      });
    });
    const confirmed = await pinCopilotConversation(conversationId, next);
    if (confirmed === null) {
      // Put it back. Leaving it pinned-looking would promise a retention
      // exemption the server never granted.
      setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, pinned: !next } : c)));
    }
  }, []);

  const completeTask = useCallback(
    async (turnIndex: number, task: CopilotTask) => {
      setTaskBusyId(task.id);
      const { approval, error } = await offerCopilotAction(
        'task.complete',
        { task_id: task.id },
        conversationRef.current,
      );
      setTaskBusyId(null);

      if (error || !approval) {
        // The tool's own refusal where there is one. "That did not work" with
        // no reason is the version of this that becomes a support ticket.
        setTurns((prev) => [
          ...prev,
          { role: 'assistant', content: error || t('copilot.actionFailed', 'That did not go through.'), failed: true },
        ]);
        return;
      }

      // An ORDINARY card, confirmed by id like any other. Clicking a row is a
      // nicer way to propose an action than typing a sentence about it; it is
      // not a way to skip the confirmation.
      setTurns((prev) =>
        prev.map((turn, i) =>
          i === turnIndex ? { ...turn, approvals: [...(turn.approvals || []), approval] } : turn,
        ),
      );
    },
    [t],
  );

  const tickStep = useCallback(async (turnIndex: number, planId: string, seq: number) => {
    const updated = await tickPlanStep(planId, seq);
    if (!updated) return;
    // The SERVER decides whether the plan is finished — it derives that from
    // the steps. Taking its answer rather than counting here is what stops the
    // panel calling a plan done with steps still open.
    setTurns((prev) => prev.map((turn, i) => (i === turnIndex ? { ...turn, plan: updated } : turn)));
  }, []);

  const runPlanStep = useCallback(
    async (turnIndex: number, step: CopilotPlanStep) => {
      if (!step.tool) return;
      setPlanBusyStep(step.seq);
      const { approval, error } = await offerCopilotAction(step.tool, step.args, conversationRef.current);
      setPlanBusyStep(null);

      if (error || !approval) {
        setTurns((prev) => [
          ...prev,
          { role: 'assistant', content: error || t('copilot.actionFailed', 'That did not go through.'), failed: true },
        ]);
        return;
      }

      // An ORDINARY card. A step in a plan is a proposal like any other; the
      // plan does not pre-approve anything in it.
      setTurns((prev) =>
        prev.map((turn, i) =>
          i === turnIndex ? { ...turn, approvals: [...(turn.approvals || []), approval] } : turn,
        ),
      );
    },
    [t],
  );

  const startNewConversation = useCallback(() => {
    conversationRef.current = null;
    setTurns([]);
    setCardStates({});
    setView('chat');
  }, []);

  const cancelRun = useCallback(async (runId: string) => {
    // Optimistic: the row is already marked server-side by the time the request
    // returns, and leaving it saying "running" for a poll interval reads as the
    // button not having worked.
    setRuns((prev) => prev.map((r) => (r.id === runId ? { ...r, status: 'cancelled' } : r)));
    await cancelCopilotRun(runId);
    setRunPollKey((k) => k + 1);
  }, []);

  const send = useCallback(
    async (question: string) => {
      const message = question.trim();
      if (!message) return;

      // Asking while a turn is streaming used to do nothing at all. Now it
      // becomes a run: the work happens on a worker, survives this tab, and
      // shows up in the tray. This is the whole of "several things at once".
      if (busy) {
        setInput('');
        const started = await startCopilotRun({
          message,
          conversation_id: conversationRef.current,
          page_context: pageContext,
        });
        if (started.error) {
          setTurns((prev) => [...prev, { role: 'assistant', content: started.error!, failed: true }]);
          return;
        }
        setRunPollKey((k) => k + 1);
        return;
      }

      setInput('');
      setBusy(true);
      setActiveSteps([]);
      setTurns((prev) => [...prev, { role: 'user', content: message }]);

      const controller = new AbortController();
      abortRef.current = controller;

      const seen: string[] = [];
      let answered = false;
      // Results arrive before the summary, so they are held and attached to the
      // turn they belong to rather than rendered as a separate bubble.
      let taskList: CopilotTaskListResult | undefined;
      let plan: CopilotPlan | undefined;
      let planOmitted = 0;

      const onEvent = (ev: CopilotEvent) => {
        if (ev.type === 'step') {
          // Two events per tool (running, then its outcome); show each once.
          if (!seen.includes(ev.tool)) {
            seen.push(ev.tool);
            setActiveSteps((prev) => [...prev, ev.tool]);
          }
        } else if (ev.type === 'result') {
          if (ev.tool === 'task.list') taskList = ev.data as CopilotTaskListResult;
          if (ev.tool === 'plan.propose') {
            const r = ev.data as CopilotPlanResult;
            plan = r.plan;
            planOmitted = r.steps_omitted || 0;
          }
        } else if (ev.type === 'summary') {
          answered = true;
          // The server opens a conversation on the first question and returns
          // its id here. Holding it is what makes the next question a
          // follow-up rather than a fresh start.
          if (ev.conversation_id) conversationRef.current = ev.conversation_id;
          setTurns((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: ev.summary,
              steps: [...seen],
              approvals: ev.pending_approvals?.length ? ev.pending_approvals : undefined,
              taskList,
              plan,
              planOmitted,
            },
          ]);
        } else if (ev.type === 'error') {
          answered = true;
          setTurns((prev) => [...prev, { role: 'assistant', content: ev.error, failed: true }]);
        }
      };

      await askCopilot(
        { message, conversation_id: conversationRef.current, page_context: pageContext },
        onEvent,
        controller.signal,
      );

      // A stream that ends without a summary is a real case — a dropped
      // connection, a killed worker. Say so rather than leaving the panel
      // looking like it is still thinking.
      if (!answered && !controller.signal.aborted) {
        setTurns((prev) => [
          ...prev,
          { role: 'assistant', content: t('copilot.noAnswer', 'I lost that answer. Try asking again.'), failed: true },
        ]);
      }

      setBusy(false);
      setActiveSteps([]);
      abortRef.current = null;
      inputRef.current?.focus();
    },
    [busy, pageContext, t],
  );

  const confirmAction = useCallback(
    async (key: string, approval: PendingApproval) => {
      if (!approval.id) return;
      setCardStates((prev) => ({ ...prev, [key]: { kind: 'running' } }));
      // The id alone. The arguments above are what the human read; the server
      // holds its own copy and runs that, so this panel cannot send a different
      // action than the one it displayed even by accident.
      const result = await confirmCopilotAction(approval.id);

      if (!result.ok) {
        setCardStates((prev) => ({
          ...prev,
          [key]: {
            kind: 'failed',
            message: result.error || t('copilot.actionFailed', 'That did not go through.'),
          },
        }));
        return;
      }

      // The tool's own undo line where it has one. It is the tool that knows how
      // to reverse itself, and saying "done" without saying how to take it back
      // is the half of the promise the card made that would go unkept.
      setCardStates((prev) => ({
        ...prev,
        [key]: {
          kind: 'done',
          message: [t('copilot.actionDone', 'Done.'), result.undo].filter(Boolean).join(' '),
        },
      }));
    },
    [t],
  );

  // Fire the seeded question once per open.
  const seededRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isOpen) {
      seededRef.current = undefined;
      return;
    }
    if (initialQuestion && seededRef.current !== initialQuestion) {
      seededRef.current = initialQuestion;
      void send(initialQuestion);
    }
  }, [isOpen, initialQuestion, send]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Spoken turns land in the SAME thread as typed ones. A separate transcript
  // pane would make the conversation two conversations, and the user would have
  // to remember which half they said out loud.
  //
  // ABOVE the early return, with every other hook. `if (!isOpen) return null`
  // sits just below, and a hook called after it runs on open and not on close —
  // React counts hooks per render, so closing the panel threw "rendered fewer
  // hooks than expected" and took the panel down. The test that closes the
  // panel is what caught it.
  const voice = useCopilotVoice(
    useCallback(
      ({ role, text }: { role: 'user' | 'agent'; text: string }) =>
        setTurns((prev) => [...prev, { role: role === 'user' ? 'user' : 'assistant', content: text }]),
      [],
    ),
    caps?.language,
  );
  voiceStopRef.current = voice.stop;

  if (!isOpen) return null;

  const unavailable = capsLoaded && (!caps || !caps.enabled);
  // What this org calls it. The default stands in until the probe answers and
  // for an org that has not renamed it — never the word "Copilot", which is a
  // category and, to most ears, another company's product.
  const name = (caps?.assistant_name || '').trim() || DEFAULT_ASSISTANT_NAME;


  // The assistant's name is joined in JS rather than interpolated through i18n.
  // A `{{name}}` placeholder renders literally wherever the interpolation is
  // not configured — and an aria-label reading "Talk to {{name}}" is worse than
  // an untranslated one, because a screen reader says it out loud.
  const voiceLabel = voice.active
    ? t('copilot.voiceStop', 'Stop talking')
    : `${t('copilot.voiceStart', 'Talk to')} ${name}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-stretch justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label={name}
      onClick={onClose}
    >
      <div
        className={
          'flex h-full w-full flex-col bg-white shadow-2xl dark:bg-gray-900 ' +
          // Full page is a width change, not a different component: the
          // conversation, the run tray and any in-flight turn stay mounted, so
          // expanding mid-answer does not restart or lose it.
          (expanded ? 'max-w-none' : 'max-w-lg')
        }
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          {/* Sparkles, not a robot and not a face. The assistant answers
              questions inside an org's own data; a persona implies more
              autonomy than the gate model actually grants. Teal is the WeaveIn
              brand token, so the panel reads as part of this product. */}
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0D9488] text-white">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h2>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {/* Says what it will actually do. A user who assumes it can act and
                  finds out otherwise mid-task trusts it less than one who was
                  told up front — and the reverse is worse: someone who believes
                  it cannot act will not read the card before confirming. */}
              {caps?.can_take_actions
                ? t('copilot.canAct', 'Answers questions, and asks before it changes anything.')
                : t('copilot.readOnly', 'Answers questions about your organization. It cannot make changes yet.')}
            </p>
          </div>
          {/* Back comes FIRST and only in history, so the way out of a
              secondary view is where the eye already is, rather than next to
              Close where it would be mistaken for it. */}
          {view === 'history' && (
            <button
              type="button"
              onClick={() => setView('chat')}
              aria-label={t('copilot.backToChat', 'Back to conversation')}
              title={t('copilot.backToChat', 'Back to conversation')}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {view === 'chat' && (
            <button
              type="button"
              onClick={() => void openHistory()}
              aria-label={t('copilot.history', 'Past conversations')}
              title={t('copilot.history', 'Past conversations')}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <History className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-label={
              expanded ? t('copilot.collapse', 'Collapse to side panel') : t('copilot.expand', 'Expand to full page')
            }
            aria-pressed={expanded}
            title={
              expanded ? t('copilot.collapse', 'Collapse to side panel') : t('copilot.expand', 'Expand to full page')
            }
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {view === 'history' ? (
          <ConversationList
            conversations={conversations}
            loading={historyLoading}
            activeId={conversationRef.current}
            onOpen={(id) => void resumeConversation(id)}
            onTogglePin={(id, next) => void togglePin(id, next)}
            onNew={startNewConversation}
            t={t}
          />
        ) : (
          <>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {unavailable && (
            <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              {t(
                'copilot.unavailable',
                "The Copilot isn't available for your account yet. Ask an administrator to check your role and your organization's enabled features.",
              )}
            </p>
          )}

          {!unavailable && turns.length === 0 && (
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <p className="flex items-center gap-2 font-medium text-gray-800 dark:text-gray-100">
                <Sparkles className="h-4 w-4" aria-hidden />
                {t('copilot.greeting', 'Ask about your programs, your caseload, or where to find something.')}
              </p>
              <ul className="space-y-1.5">
                {[
                  t('copilot.example1', 'Where do I record an outreach call?'),
                  t('copilot.example2', 'How many members are on my caseload?'),
                  t('copilot.example3', 'What programs am I assigned to?'),
                ].map((example) => (
                  <li key={example}>
                    <button
                      type="button"
                      onClick={() => void send(example)}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-left hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                    >
                      {example}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {turns.map((turn, i) => (
            <div key={i} className={turn.role === 'user' ? 'text-right' : ''}>
              <div
                className={
                  turn.role === 'user'
                    ? 'inline-block max-w-[85%] rounded-lg bg-gray-900 px-3 py-2 text-left text-sm text-white dark:bg-gray-100 dark:text-gray-900'
                    : turn.failed
                      ? 'text-sm text-red-700 dark:text-red-300'
                      : 'text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-100'
                }
              >
                {turn.content}
              </div>
              {/* The tool's own data, above the cards, because a card raised
                  from a row should appear under the thing it came from. */}
              {turn.role === 'assistant' && turn.plan && (
                <PlanCard
                  plan={turn.plan}
                  omitted={turn.planOmitted || 0}
                  busyStep={planBusyStep}
                  onTick={(seq) => void tickStep(i, turn.plan!.id, seq)}
                  onRun={(step) => void runPlanStep(i, step)}
                  t={t}
                />
              )}
              {turn.role === 'assistant' && turn.taskList && (
                <TaskListCard
                  result={turn.taskList}
                  busyId={taskBusyId}
                  onComplete={(task) => void completeTask(i, task)}
                  t={t}
                />
              )}
              {turn.role === 'assistant' &&
                turn.approvals?.map((approval, j) => {
                  // No id means the server recorded no offer, so Confirm would
                  // have nothing to claim. A button that cannot work reads as a
                  // product ignoring the person who pressed it.
                  if (!approval.id) return null;
                  const key = `${i}:${j}`;
                  return (
                    <ConfirmationCard
                      key={key}
                      approval={approval}
                      state={cardStates[key] || { kind: 'idle' }}
                      onConfirm={() => void confirmAction(key, approval)}
                      onDismiss={() => setCardStates((prev) => ({ ...prev, [key]: { kind: 'dismissed' } }))}
                    />
                  );
                })}
              {turn.role === 'assistant' && turn.steps && turn.steps.length > 0 && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {turn.steps.map(stepLabel).join(' · ')}
                </p>
              )}
            </div>
          ))}

          {/* Voice state, said plainly. "Listening" and "Speaking" are the only
              two a user needs, and they need them because a microphone that is
              on and silent is indistinguishable from one that is off. */}
          {voice.active && (
            <p className="flex items-center gap-2 text-xs text-[#0D9488]">
              {voice.state === 'speaking' ? (
                <Volume2 className="h-3 w-3" aria-hidden />
              ) : voice.state === 'connecting' ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Mic className="h-3 w-3 animate-pulse" aria-hidden />
              )}
              <span role="status">
                {voice.state === 'connecting'
                  ? t('copilot.voiceConnecting', 'Connecting…')
                  : voice.state === 'speaking'
                    ? t('copilot.voiceSpeaking', 'Speaking')
                    : t('copilot.voiceListening', 'Listening — say "stop" to end')}
              </span>
            </p>
          )}

          {voice.error && (
            <p className="text-xs text-red-700 dark:text-red-300">{voice.error}</p>
          )}

          {/* A spoken request for a write ends here.
              Voice can never approve anything — there is no way to show a human
              the exact arguments in a sentence, and a spoken "yes" is not a
              record of what was agreed. So the action leaves the conversation
              and appears as the SAME card the typed surface uses.

              Confirming sends only the server's id: it holds the tool and the
              arguments it showed, so what runs is what was read. */}
          {voice.approvals.map((approval) => {
            const outcome = voice.results[approval.id];
            return (
              <ConfirmationCard
                key={approval.id}
                approval={{
                  tool: approval.tool,
                  args: approval.args,
                  gate: approval.gate,
                  reversibility: approval.reversibility,
                  message: approval.message,
                }}
                state={
                  outcome
                    ? outcome.ok
                      ? { kind: 'done', message: [t('copilot.actionDone', 'Done.'), outcome.undo].filter(Boolean).join(' ') }
                      : { kind: 'failed', message: outcome.error || t('copilot.actionFailed', 'That did not go through.') }
                    : { kind: 'idle' }
                }
                onConfirm={() => voice.confirm(approval.id)}
                onDismiss={() => voice.dismiss(approval.id)}
              />
            );
          })}

          {busy && (
            <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
              <p className="flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                {activeSteps.length === 0
                  ? t('copilot.thinking', 'Thinking…')
                  : stepLabel(activeSteps[activeSteps.length - 1])}
              </p>
            </div>
          )}
        </div>

        <RunTray runs={runs} onCancel={cancelRun} t={t} />

        <form
          className="flex items-center gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700"
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
        >
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={unavailable}
            autoFocus
            // Browsers offer to save anything that looks like a form field;
            // a question is not a credential.
            autoComplete="off"
            data-1p-ignore
            placeholder={t('copilot.placeholder', 'Ask a question…')}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          {/* Shown only when the SERVER says voice is on. `voice_enabled`
              answers from the operator switch and whether speech is configured
              at all, so turning it off centrally removes the control on the
              next probe rather than leaving a button that connects to
              nothing. */}
          {caps?.voice_enabled && (
            <button
              type="button"
              onClick={() => (voice.active ? voice.stop() : voice.start())}
              disabled={unavailable}
              aria-label={voiceLabel}
              aria-pressed={voice.active}
              title={voiceLabel}
              className={
                'rounded-md p-2 transition-colors disabled:opacity-40 ' +
                (voice.active
                  ? 'bg-[#0D9488] text-white hover:bg-[#0F766E]'
                  : 'border border-gray-300 text-gray-600 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300')
              }
            >
              {voice.active ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}
          <button
            type="submit"
            disabled={unavailable || !input.trim()}
            aria-label={t('copilot.send', 'Send')}
            className="rounded-md bg-gray-900 p-2 text-white disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default CopilotPanel;
